import { useEffect, useMemo, useState } from 'react';
import { useConfig } from '../context/ConfigContext';
import BrandLogo from './BrandLogo';
import Modal from './Modal';
import PosStaffList from './PosStaffList';
import PosClientRoles from './PosClientRoles';
import PosClientSubscriptionTab from './PosClientSubscriptionTab';
import PosClientFeatureSettingsTab from './PosClientFeatureSettingsTab';
import { spinnerRing } from '../utils/bookingUtils';
import {
  savePosClient,
  savePosClientStaff,
  savePosClientRoles,
  fetchClientStaff,
  fetchClientRoles,
  suggestClientId,
  validateClientId,
  clientEmailDomain,
  fetchClientFeatureSettings,
  saveClientFeatureSettings,
} from '../utils/posClientUtil';
import {
  subscribeSubscriptionPlans,
  fetchClientSubscription,
  saveClientSubscription,
  subscriptionChoiceChanged,
} from '../utils/posSubscriptionUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { posVatBreakdown, fetchPosRoleOverrideIds } from '../utils/posUtil';
import { formatMoney, formatAmount } from '../utils/currencyUtil';
import {
  POS_VAT_MODES,
  POS_VAT_MODE_NONE,
  POS_VAT_MODE_INCLUSIVE,
  POS_VAT_RATE_DEFAULT,
  POS_VAT_RATE_MAX,
  POS_DEFAULT_TIMEZONE,
  POS_RESERVE_ENABLED_KEY,
} from '../constants/posConst';
import '../css/PosAdmin.css';

/**
 * The basket the VAT panel works its example through.
 *
 * 140 rather than a round 100 on purpose: at 12% a hundred splits into figures
 * that read as percentages rather than as money, and the point of the preview
 * is to show real amounts.
 */
const VAT_PREVIEW_SUBTOTAL = 140;

/**
 * The sections of the registration form.
 *
 * Info is the client itself; Staff is who may stand at its till; Roles is what
 * each of them may do once there; VAT is what its customers are charged.
 * Separate tabs because they are edited at different times by different
 * people — a club's phone number changes once, its staff list changes every
 * season, and its roles and tax are set up once and then largely left alone.
 *
 * VAT LIVES HERE rather than on the Config screen beside it, because Config
 * holds settings shared by EVERY client and VAT is not one: a deployment can
 * run a VAT-registered club next to one that is not. This form is the only
 * admin screen that already addresses one client at a time.
 *
 * Subscriptions is which plan the client is on and until when — set here by
 * an administrator, or activated from an application the client sent in.
 *
 * Feature Settings is which optional parts of the till this client gets (the
 * reserve stock, for now). Here rather than on the club's own settings screen
 * because it is the administrator's call, not the club's.
 */
const SECTIONS = [
  { key: 'info', label: 'Info' },
  { key: 'staff', label: 'Staff' },
  { key: 'roles', label: 'Roles' },
  { key: 'vat', label: 'VAT' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'features', label: 'Feature Settings' },
];

/** A new client's feature switches: everything on, as an unset client reads. */
const DEFAULT_FEATURES = { [POS_RESERVE_ENABLED_KEY]: true };

/** The empty subscription choice: "No subscription". */
const NO_SUBSCRIPTION = { planId: '', startDate: '', endDate: '' };

/**
 * Registers a new client, or edits an existing one.
 *
 * THE ID IS PERMANENT. It is the client's URL and the key its entire trading
 * subtree hangs from, so it can be set once and then only read. Editing shows
 * it as text rather than a disabled input, which reads as "this is a fact
 * about the client" rather than "this field is broken".
 *
 * @param {object} props
 * @param {object|null} props.client - the client being edited, or null to add.
 * @param {(clientId: string) => void} props.onSaved - called after a
 *   successful write, with the id.
 * @param {() => void} props.onCancel
 * @param {string} [props.signedInEmail] - the administrator making the change,
 *   recorded on the client's audit trail when the VAT settings move.
 * @returns {JSX.Element}
 */
function PosClientForm({ client = null, onSaved, onCancel, signedInEmail = '' }) {
  const isEdit = Boolean(client);
  // Only for the currency symbol in the VAT preview. /admin runs on global
  // config alone, which is exactly where the symbol lives.
  const { config } = useConfig();

  const [section, setSection] = useState('info');
  const [clientId, setClientId] = useState(client?.id || '');
  // Tracks whether the id was typed by hand. Until it is, it follows the name
  // — which is what an administrator naming a club expects — but a hand-typed
  // id is never overwritten by later edits to the name.
  const [idTouched, setIdTouched] = useState(isEdit);
  const [name, setName] = useState(client?.name || '');
  const [email, setEmail] = useState(client?.email || '');
  const [contact, setContact] = useState(client?.contact || '');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(client?.logoUrl || null);

  // VAT. An unset client reads as "No VAT" — anything else would start
  // charging tax the moment this form is saved for an unrelated reason.
  const [vatMode, setVatMode] = useState(client?.vatMode || POS_VAT_MODE_NONE);
  // Kept as a STRING so the field can be emptied while it is being retyped.
  // A blank is refused on save rather than defaulted, so a half-finished
  // setting cannot become a rate nobody chose — see validateVatSettings.
  const [vatRate, setVatRate] = useState(
    client?.vatRate ? String(client.vatRate) : String(POS_VAT_RATE_DEFAULT),
  );

  // Rows of { email, roleId } — an address alone is not enough any more, since
  // the role is what the till enforces.
  const [staffEmails, setStaffEmails] = useState([]);
  const [staffLoading, setStaffLoading] = useState(isEdit);
  // If the existing staff could not be read, the field below is empty for a
  // reason that has nothing to do with what is stored — and saving it would
  // write that emptiness back, revoking everyone's access. Tracked so the save
  // can leave the staff list alone instead.
  const [staffLoadError, setStaffLoadError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(isEdit);
  // Same protection as staffLoadError, for the same reason: an empty list that
  // is empty because the read failed must not be written back over the roles
  // this client's staff are actually held under.
  const [rolesLoadError, setRolesLoadError] = useState(null);
  // Roles whose rights the club's store admin has changed — flagged on the
  // Roles tab. Purely informational, so a failed read just flags nothing.
  const [customisedRoleIds, setCustomisedRoleIds] = useState(() => new Set());

  // Subscriptions. The timezone lives on the profile; the plan and its dates
  // on the client's own subscription record.
  const [timezone, setTimezone] = useState(client?.timezone || POS_DEFAULT_TIMEZONE);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [existingSub, setExistingSub] = useState(null);
  const [subLoading, setSubLoading] = useState(isEdit);
  // Same protection as staffLoadError: a subscription that could not be read
  // must not be saved back as "no subscription".
  const [subLoadError, setSubLoadError] = useState(null);
  const [subChoice, setSubChoice] = useState(NO_SUBSCRIPTION);

  // Feature switches. `existingFeatures` is what was stored when the form
  // opened, so an untouched tab saves as "no change".
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [existingFeatures, setExistingFeatures] = useState(null);
  const [featuresLoading, setFeaturesLoading] = useState(isEdit);
  // Same protection as staffLoadError: switches that could not be read must
  // not be saved back as the defaults.
  const [featuresLoadError, setFeaturesLoadError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => subscribeSubscriptionPlans(
    (list) => { setPlans(list); setPlansLoading(false); },
    () => setPlansLoading(false),
  ), []);

  // The stored subscription, for an edit — seeds the tab's choice so an
  // untouched tab saves as "no change".
  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    setSubLoading(true);
    fetchClientSubscription(client.id)
      .then((current) => {
        if (!active) return;
        setExistingSub(current);
        setSubChoice(current
          ? { planId: current.planId, startDate: current.startDate, endDate: current.endDate }
          : NO_SUBSCRIPTION);
        setSubLoadError(null);
      })
      .catch((error) => {
        console.error('Could not load subscription:', error);
        if (active) setSubLoadError(error);
      })
      .finally(() => { if (active) setSubLoading(false); });
    return () => { active = false; };
  }, [isEdit, client]);

  // The stored feature switches, for an edit.
  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    setFeaturesLoading(true);
    fetchClientFeatureSettings(client.id)
      .then((current) => {
        if (!active) return;
        setExistingFeatures(current);
        setFeatures(current);
        setFeaturesLoadError(null);
      })
      .catch((error) => {
        console.error('Could not load feature settings:', error);
        if (active) setFeaturesLoadError(error);
      })
      .finally(() => { if (active) setFeaturesLoading(false); });
    return () => { active = false; };
  }, [isEdit, client]);

  // Existing staff, for an edit. A new client has none by definition, so the
  // fetch is skipped rather than run to prove it.
  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    setStaffLoading(true);
    fetchClientStaff(client.id)
      .then((list) => { if (active) { setStaffEmails(list); setStaffLoadError(null); } })
      .catch((error) => {
        console.error('Could not load staff list:', error);
        if (active) setStaffLoadError(error);
      })
      .finally(() => { if (active) setStaffLoading(false); });
    return () => { active = false; };
  }, [isEdit, client]);

  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    fetchPosRoleOverrideIds(client.id)
      .then((ids) => { if (active) setCustomisedRoleIds(ids); })
      .catch(() => {});
    return () => { active = false; };
  }, [isEdit, client]);

  // Existing roles, for an edit. Read separately from the staff list rather
  // than in one pass, so one failing read does not blank the other tab.
  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    setRolesLoading(true);
    fetchClientRoles(client.id)
      .then((list) => { if (active) { setRoles(list); setRolesLoadError(null); } })
      .catch((error) => {
        console.error('Could not load roles:', error);
        if (active) setRolesLoadError(error);
      })
      .finally(() => { if (active) setRolesLoading(false); });
    return () => { active = false; };
  }, [isEdit, client]);

  // Object URLs leak for the lifetime of the document unless revoked, and this
  // form can have several logos tried in one sitting.
  useEffect(() => () => {
    if (logoPreview?.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const effectiveId = useMemo(
    () => (idTouched ? clientId : suggestClientId(name)),
    [idTouched, clientId, name],
  );

  // How many staff rows hold each role, so the Roles tab can refuse to delete
  // one that is in use. Counted from the rows as they stand in the form rather
  // than from the database: an assignment made a moment ago and not yet saved
  // still means someone is relying on that role.
  const roleAssignmentCounts = useMemo(() => staffEmails.reduce((counts, row) => {
    if (!row?.roleId) return counts;
    return { ...counts, [row.roleId]: (counts[row.roleId] || 0) + 1 };
  }, {}), [staffEmails]);

  // The VAT panel's worked example, recomputed as the mode and rate are
  // edited. Runs through posVatBreakdown — the same function the till, the
  // receipt and the stored order use — so what this previews is what will
  // actually be charged, rather than a second implementation that agrees with
  // it today.
  const vatPreview = useMemo(() => {
    if (vatMode === POS_VAT_MODE_NONE) return null;
    const preview = posVatBreakdown(VAT_PREVIEW_SUBTOTAL, { vatMode, vatRate });
    // Nothing to show while the rate is blank or being retyped; the field's
    // own error on save is where an unusable rate gets reported.
    return preview.applies ? preview : null;
  }, [vatMode, vatRate]);

  // Shown in the Staff tab so the rule is visible while addresses are being
  // typed, rather than only in the error after a refused save. Follows the id
  // as it is edited, since that is what the domain is derived from.
  const domain = clientEmailDomain(effectiveId);

  /**
   * Takes the chosen file and shows it immediately.
   *
   * Compression happens on save rather than here: re-encoding on every pick
   * would make the preview lag behind the click for a large photo, and the
   * file is not stored until the form is submitted anyway.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event
   * @returns {void}
   */
  const handleLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  /**
   * The tab's choice in the shape saveClientSubscription takes, or null for
   * "No subscription". A plan that has since been deleted is rebuilt from the
   * stored copy, so keeping a client on it is still possible.
   *
   * @returns {{plan: object, startDate: string, endDate: string}|null}
   */
  const buildSubscriptionChoice = () => {
    if (!subChoice.planId) return null;
    const plan = plans.find((option) => option.id === subChoice.planId)
      || (existingSub?.planId === subChoice.planId ? {
        id: existingSub.planId,
        name: existingSub.planName,
        price: existingSub.price,
        durationValue: existingSub.durationValue,
        durationUnit: existingSub.durationUnit,
        isTrial: existingSub.isTrial,
      } : null);
    return { plan, startDate: subChoice.startDate, endDate: subChoice.endDate };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      // Validated before the write so a bad id is reported against the field
      // the administrator typed, not as a database error.
      const id = validateClientId(effectiveId);
      await savePosClient({
        clientId: id,
        name,
        email,
        contact,
        logoFile,
        vatMode,
        vatRate,
        timezone,
        existing: isEdit ? client : null,
        actor: signedInEmail,
      });

      // Staff are written separately: savePosClient deliberately touches only
      // the profile, so a failed staff write cannot roll back a saved client
      // (and vice versa). savePosClientStaff validates every address and
      // checks every other client's list before it writes anything.
      //
      // SKIPPED ENTIRELY when the existing list could not be read. The field
      // is empty in that case because the read failed, not because the client
      // has no staff, and writing it back would revoke everyone's access to
      // fix a phone number.
      // ROLES BEFORE STAFF, deliberately. A staff row can be assigned a role
      // created in the same sitting, and savePosClientStaff refuses an
      // assignment to a role that does not exist — so the roles have to be in
      // the database before the assignments are checked against them.
      //
      // Each is written separately, for the same reason as the profile: they
      // are self-contained decisions, and a failure in one must not undo
      // another that has already succeeded. Both are skipped on a failed read.
      const savedRoles = rolesLoadError ? null : await savePosClientRoles(id, roles);
      const emails = staffLoadError ? null : await savePosClientStaff(id, staffEmails);

      // Last, and only when it actually changed: saving a phone number must
      // not rewrite the plan or put a "subscription set" line on the trail.
      // Skipped entirely on a failed read, for the same reason as staff.
      let subscriptionNote = '';
      if (!subLoadError) {
        const choice = buildSubscriptionChoice();
        if (subscriptionChoiceChanged(existingSub, choice)) {
          await saveClientSubscription(id, choice, { existing: existingSub, actor: signedInEmail });
        }
      } else {
        subscriptionNote = ' Its subscription was left untouched, because it could not be read when this form opened.';
      }

      // Written only when a switch moved; skipped on a failed read.
      const featuresNote = featuresLoadError
        ? ' Its feature settings were left untouched, because they could not be read when this form opened.'
        : '';
      if (!featuresLoadError) {
        await saveClientFeatureSettings(id, features, {
          existing: isEdit ? existingFeatures : null,
          actor: signedInEmail,
        });
      }

      const staffNote = emails === null
        ? ' Its staff list was left untouched, because it could not be read when this form opened.'
        : (emails.length === 0 ? ' No staff accounts yet, so nobody can sign in there until you add one.' : '');

      const rolesNote = savedRoles === null
        ? ' Its roles were left untouched, because they could not be read when this form opened.'
        : '';

      setModal({
        type: (emails === null || savedRoles === null || subLoadError || featuresLoadError) ? 'warning' : 'success',
        title: isEdit ? 'Client updated' : 'Client registered',
        message: isEdit
          ? `${name || id} has been updated.${staffNote}${rolesNote}${subscriptionNote}${featuresNote}`
          : `${name || id} is live at /${id}.${staffNote}${rolesNote}${subscriptionNote}${featuresNote}`,
        onClose: () => { setModal(null); onSaved(id); },
      });
    } catch (error) {
      console.error('Saving client failed:', error);
      setModal({
        type: 'error',
        title: 'Could not save',
        message: describeWriteError(error, { what: 'this client', path: `posClients/${effectiveId}` }),
        onClose: () => setModal(null),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pa-screen">
      <header className="pa-screen-head">
        <div>
          <h1 className="pa-screen-title">{isEdit ? `Edit ${client.name || client.id}` : 'Client Registration'}</h1>
          <p className="pa-screen-sub">
            {isEdit
              ? 'The client id cannot change — it is this client\'s address and the key its data hangs from.'
              : 'A new client gets its own till, its own stock and its own takings.'}
          </p>
        </div>
        <button type="button" className="pa-btn pa-btn-quiet" onClick={onCancel}>Back to clients</button>
      </header>

      <div className="pa-tabs" role="tablist" aria-label="Client sections">
        {SECTIONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={section === key}
            className={`pa-tab${section === key ? ' active' : ''}`}
            onClick={() => setSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <form className="pa-form" onSubmit={handleSubmit}>
        {section === 'info' && (
          <div className="pa-form-grid">
            <label className="pa-field">
              <span className="pa-field-label">Client id</span>
              {isEdit ? (
                <span className="pa-field-static"><code className="pa-code">{client.id}</code></span>
              ) : (
                <>
                  <input
                    className="pa-input"
                    value={effectiveId}
                    onChange={(event) => { setIdTouched(true); setClientId(event.target.value); }}
                    placeholder="court-district"
                    autoComplete="off"
                    spellCheck="false"
                    disabled={saving}
                  />
                  <span className="pa-field-hint">
                    Becomes the address: <code className="pa-code">/{effectiveId || '<client-id>'}</code>.
                    Lower-case letters, numbers and hyphens. Permanent once saved.
                  </span>
                </>
              )}
            </label>

            <label className="pa-field">
              <span className="pa-field-label">Client name</span>
              <input
                className="pa-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Court District Cebu"
                disabled={saving}
              />
              <span className="pa-field-hint">Shown on the till header, the sign-in card and every receipt.</span>
            </label>

            <label className="pa-field">
              <span className="pa-field-label">Client email address</span>
              <input
                className="pa-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="hello@club.com"
                disabled={saving}
              />
              <span className="pa-field-hint">A contact address for the club. Not a sign-in — staff accounts live under Staff.</span>
            </label>

            <label className="pa-field">
              <span className="pa-field-label">Client contact info</span>
              <input
                className="pa-input"
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                placeholder="+63 900 000 0000"
                disabled={saving}
              />
            </label>

            <div className="pa-field pa-field-wide">
              <span className="pa-field-label">Client logo</span>
              <div className="pa-logo-row">
                <BrandLogo src={logoPreview} alt={name || 'Client logo'} className="pa-logo-preview" />
                <div>
                  <input
                    className="pa-file"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogo}
                    disabled={saving}
                  />
                  <span className="pa-field-hint">
                    Compressed to at most 512px and 60KB before it is stored, because every
                    till session for this client downloads it.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {section === 'staff' && (
          <div className="pa-form-grid">
            <div className="pa-field pa-field-wide">
              <span className="pa-field-label">Staff accounts</span>
              {staffLoading ? (
                <div className="pa-loading pa-loading-inline">{spinnerRing('Loading staff')}</div>
              ) : staffLoadError ? (
                <>
                  <p className="pa-field-hint">
                    This client&rsquo;s staff list could not be read: {staffLoadError.message}
                  </p>
                  <p className="pa-field-hint">
                    It is being left exactly as it is — saving this form will change the client&rsquo;s
                    details only. Usually this means the database rules have not been deployed, or
                    this account is not one the rules name as an administrator.
                  </p>
                </>
              ) : (
                <>
                  <PosStaffList
                    values={staffEmails}
                    onChange={setStaffEmails}
                    roles={roles}
                    placeholder={`name@${domain}`}
                    disabled={saving}
                  />
                  <span className="pa-field-hint">
                    These are the ONLY accounts that may sign in at this client&rsquo;s till — an
                    account that is not listed is refused before the password is even checked, and
                    staff sign in every time they open it. Only system@gmail.com can open any
                    client without being listed.
                  </span>
                  <span className="pa-field-hint">
                    {roles.length === 0
                      ? 'The role decides which screens that account sees once it is in. No roles are defined yet — add one under Roles and it will appear in the list above.'
                      : 'The role decides which screens that account sees once it is in. An account left on "No role" keeps full access, so existing staff are not shut out the day roles are introduced — assign one to start restricting it.'}
                  </span>
                  <span className="pa-field-hint">
                    Any valid email address works. <code className="pa-code">name@{domain}</code> is
                    only a suggestion — it keeps each client&rsquo;s staff easy to tell apart. An
                    address already registered to a different client is refused on save, whatever
                    its domain. Each must also exist as a Firebase Auth user before it can sign in.
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {section === 'roles' && (
          <div className="pa-form-grid">
            <div className="pa-field pa-field-wide">
              <span className="pa-field-label">Roles</span>
              <PosClientRoles
                roles={roles}
                onChange={setRoles}
                assignedCounts={roleAssignmentCounts}
                customisedIds={customisedRoleIds}
                loading={rolesLoading}
                loadError={rolesLoadError}
                disabled={saving}
              />
              {!rolesLoading && !rolesLoadError && (
                <span className="pa-field-hint">
                  A role is a name and a set of permissions, defined per client. Roles are
                  saved with the rest of this form — nothing is written until you save.
                </span>
              )}
            </div>
          </div>
        )}

        {section === 'vat' && (
          <div className="pa-form-grid">
            <div className="pa-field pa-field-wide">
              <span className="pa-field-label">VAT treatment</span>
              <div className="pa-choices" role="radiogroup" aria-label="VAT treatment">
                {POS_VAT_MODES.map((mode) => (
                  <label key={mode.key} className={`pa-choice${vatMode === mode.key ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="vatMode"
                      value={mode.key}
                      checked={vatMode === mode.key}
                      onChange={() => setVatMode(mode.key)}
                      disabled={saving}
                    />
                    <span className="pa-choice-text">
                      <span className="pa-choice-label">{mode.label}</span>
                      <span className="pa-choice-desc">{mode.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <span className="pa-field-hint">
                Applies to this client only, from the next sale onward. Orders already recorded keep
                the figures they were charged at, so an old receipt never restates itself.
              </span>
            </div>

            {/* Hidden on No VAT rather than disabled: a greyed-out rate beside
                "No VAT" invites the reading that it is still in force
                somewhere. */}
            {vatMode !== POS_VAT_MODE_NONE && (
              <label className="pa-field">
                <span className="pa-field-label">VAT rate (%)</span>
                <input
                  className="pa-input"
                  type="number"
                  min="0"
                  max={POS_VAT_RATE_MAX}
                  step="0.01"
                  value={vatRate}
                  onChange={(event) => setVatRate(event.target.value)}
                  inputMode="decimal"
                  disabled={saving}
                />
                <span className="pa-field-hint">
                  12 is the Philippine rate. Up to {POS_VAT_RATE_MAX}, two decimal places.
                </span>
              </label>
            )}

            {/* WORKED THROUGH, not described. "Inclusive" and "exclusive" are
                one letter apart and mean opposite things to the customer's
                wallet; the fastest way to be sure which has been chosen is to
                see what a real basket would come to under it. */}
            {vatPreview && (
              <div className="pa-field pa-field-wide">
                <span className="pa-field-label">What the till will show</span>
                <div className="pa-vat-preview">
                  <div className="pa-vat-row">
                    <span>Subtotal</span>
                    <span>{formatMoney(vatPreview.subtotal, config)}</span>
                  </div>
                  {vatMode === POS_VAT_MODE_INCLUSIVE && (
                    <div className="pa-vat-row pa-vat-part">
                      <span>Item cost ({formatAmount(vatPreview.netShare)}%)</span>
                      <span>{formatMoney(vatPreview.net, config)}</span>
                    </div>
                  )}
                  <div className={`pa-vat-row${vatMode === POS_VAT_MODE_INCLUSIVE ? ' pa-vat-part' : ''}`}>
                    <span>VAT ({formatAmount(vatPreview.rate)}%)</span>
                    <span>{formatMoney(vatPreview.vat, config)}</span>
                  </div>
                  <div className="pa-vat-row pa-vat-total">
                    <span>TOTAL</span>
                    <span>{formatMoney(vatPreview.total, config)}</span>
                  </div>
                </div>
                <span className="pa-field-hint">
                  On a {formatMoney(VAT_PREVIEW_SUBTOTAL, config)} basket.{' '}
                  {vatMode === POS_VAT_MODE_INCLUSIVE
                    ? 'The customer still pays the subtotal — the tax was already inside it.'
                    : 'The customer pays more than the subtotal, because the tax was added to it.'}
                </span>
              </div>
            )}
          </div>
        )}

        {section === 'subscriptions' && (
          <PosClientSubscriptionTab
            plans={plans}
            plansLoading={plansLoading}
            existing={existingSub}
            loading={subLoading}
            loadError={subLoadError}
            value={subChoice}
            onChange={setSubChoice}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            config={config}
            disabled={saving}
          />
        )}

        {section === 'features' && (
          <PosClientFeatureSettingsTab
            value={features}
            onChange={setFeatures}
            loading={featuresLoading}
            loadError={featuresLoadError}
            disabled={saving}
          />
        )}

        <div className="pa-form-actions">
          <button type="button" className="pa-btn pa-btn-quiet" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="pa-btn pa-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Register client')}
          </button>
        </div>
      </form>

      {saving && <div className="pa-saving">{spinnerRing('Saving')}</div>}

      <Modal
        isOpen={Boolean(modal)}
        onClose={modal?.onClose || (() => setModal(null))}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
      />
    </div>
  );
}

export default PosClientForm;
